<script setup lang="ts">
/**
 * Internal linking SEO — autres associations proches (SEO sprint v2, 2026-06-15).
 * Affiche jusqu'à 10 assos liées en bas de la page /associations/[slug].
 *
 * Karim 2026-06-15 : utilise RechercheOrganizationCard pour la cohérence
 * visuelle avec /associations (mêmes infos : banner + logo + nom
 * + adresse + chip catégorie + nombre d'activités).
 */
interface RelatedOrganization {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  cover_image: string | null;
  category: string | null;
  city: string | null;
  description: string | null;
  full_address: string | null;
  objet_social: string | null;
  activities_count: number;
}

const props = defineProps<{
  slug: string;
  title?: string;
}>();

const config = useRuntimeConfig();
const apiUrl = config.public.apiBaseUrl;

const { data } = await useAsyncData(
  `organizations-related-${props.slug}`,
  async () => {
    try {
      const res = await $fetch<{ success: boolean; data: RelatedOrganization[] }>(
        `${apiUrl}/api/organizations/public/related/${props.slug}`,
      );
      return res.data || [];
    } catch {
      return [];
    }
  },
  { server: true, lazy: false },
);

const orgs = computed(() => (data.value || []).slice(0, 10));

const scroller = ref<HTMLElement | null>(null);
const scrollBy = (dir: "left" | "right") => {
  if (!scroller.value) return;
  const amount = scroller.value.clientWidth * 0.8;
  scroller.value.scrollBy({
    left: dir === "left" ? -amount : amount,
    behavior: "smooth",
  });
};
</script>

<template>
  <section v-if="orgs.length > 0" class="py-12 sm:py-16">
    <UContainer>
      <div class="relative mb-6 sm:mb-8">
        <div>
          <h2 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {{ title || $t("association.suggestions.associationsTitre") }}
          </h2>
          <p class="text-gray-500 text-sm sm:text-base">
            {{ $t("association.suggestions.associationsSousTitre") }}
          </p>
        </div>
        <div v-if="orgs.length > 5" class="hidden md:flex absolute right-0 bottom-0 items-center gap-2">
          <button type="button" class="size-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-center text-gray-700" :aria-label="$t('association.suggestions.defilerGauche')" @click="scrollBy('left')">
            <UIcon name="i-lucide-arrow-left" class="size-4" />
          </button>
          <button type="button" class="size-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-center text-gray-700" :aria-label="$t('association.suggestions.defilerDroite')" @click="scrollBy('right')">
            <UIcon name="i-lucide-arrow-right" class="size-4" />
          </button>
        </div>
      </div>

      <div ref="scroller" class="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-hide" style="scroll-behavior: smooth;">
        <div
          v-for="o in orgs"
          :key="o.id"
          class="shrink-0 snap-start w-[300px] sm:w-[340px]"
        >
          <RechercheOrganizationCard :org="o" />
        </div>
      </div>
    </UContainer>
  </section>
</template>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
